import { useState, useEffect } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity, Image, Alert, Platform, ActivityIndicator, TextInput,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { preparePhoto, toDataUri } from '../../utils/webPhoto';
import { api } from '../../services/api';
import { Button } from '../../components/Button';
import { RD, fonts } from '../../theme/colors';

// A storage drop-off goes into OUR unit, so the proof is deliberately light: a couple of
// photos of the item where it now sits, and the DRIVER's own signature. There is nobody at
// a storage unit to sign for it, which is why this never asks for a customer signature.
const MIN_PHOTOS = 2;

// Same conditional import as SignatureScreen: react-native-signature-canvas needs
// react-native-webview, which does not exist on Expo web, so web falls back to a typed name.
let SignatureCanvas: any = null;
if (Platform.OS !== 'web') {
  try { SignatureCanvas = require('react-native-signature-canvas').default; } catch { /* typed fallback */ }
}

export function StorageDropOffScreen({ route, navigation }: any) {
  const { id, productTitle } = route.params || {};
  const PERSIST_KEY = `cp_storage_dropoff_${id}`;
  const [photos, setPhotos] = useState<string[]>([]);
  const [video, setVideo] = useState<string | null>(null);
  const [signature, setSignature] = useState<string | null>(null);
  const [typedName, setTypedName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [restored, setRestored] = useState(false);

  // Restore captured media after a picker-triggered reload, then persist on change. Photos +
  // video are data: URIs; tiered under the ~5MB quota: photos+video → photos-only → none.
  useEffect(() => {
    AsyncStorage.getItem(PERSIST_KEY).then(raw => {
      if (raw) { try { const s = JSON.parse(raw); if (Array.isArray(s.photos)) setPhotos(s.photos); if (s.video) setVideo(s.video); } catch {} }
      setRestored(true);
    }).catch(() => setRestored(true));
  }, [id]);
  useEffect(() => {
    if (!restored) return;
    // 🚨 THE LAST TIER USED TO BE `photos: []`, WHICH THROWS AWAY EVERY PHOTO THE DRIVER
    // HAS TAKEN. This screen exists to be filled in standing at a storage unit, and the
    // iOS PWA reloads whenever the camera opens - so "too big" silently meant "start
    // again", forever, on a screen that will not submit under two photos. Photos now come
    // off the snapshot oldest-first, one at a time, so as much survives as will fit.
    const vid = video && video.startsWith('data:') ? video : null;
    const QUOTA = 4_500_000;
    let kept = photos.filter(u => u.startsWith('data:'));
    let keepVideo: string | null = vid;
    const size = () => JSON.stringify({ photos: kept, video: keepVideo }).length;
    if (size() > QUOTA) keepVideo = null;            // the video is by far the biggest thing
    let guard = kept.length + 1;
    while (size() > QUOTA && kept.length > 0 && guard-- > 0) kept = kept.slice(1);
    AsyncStorage.setItem(PERSIST_KEY, JSON.stringify({ photos: kept, video: keepVideo })).catch(() => {});
  }, [restored, photos, video]);

  const pickPhoto = async () => {
    try {
      const lib = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ImagePicker.MediaTypeOptions.Images, quality: 0.8 });
      if (!lib.canceled && lib.assets?.[0]) { const d = await preparePhoto(lib.assets[0].uri); setPhotos(p => [...p, d]); return; }
      if (lib.canceled) return;
    } catch { /* fall through to the camera; the alert below covers both branches */ }
    try {
      const cam = await ImagePicker.launchCameraAsync({ quality: 0.8 });
      if (!cam.canceled && cam.assets?.[0]) { const d = await preparePhoto(cam.assets[0].uri); setPhotos(p => [...p, d]); return; }
      if (cam.canceled) return;
      // Neither branch produced a file and neither threw. Saying nothing here is what makes
      // a driver tap the button five times and then text us that the app is broken.
      Alert.alert('Photo not added', 'That photo did not come through. Please try again.');
    } catch { Alert.alert('Camera unavailable', 'Could not open the camera or library. Please allow photo and camera access for this site, then try again.'); }
  };

  const pickVideo = async () => {
    try {
      const lib = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ImagePicker.MediaTypeOptions.Videos, quality: 0.7, videoMaxDuration: 120 });
      if (!lib.canceled && lib.assets?.[0]) { setVideo(await toDataUri(lib.assets[0].uri)); return; }
      if (lib.canceled) return;
    } catch {}
    try {
      const cam = await ImagePicker.launchCameraAsync({ mediaTypes: ImagePicker.MediaTypeOptions.Videos, videoMaxDuration: 120 });
      if (!cam.canceled && cam.assets?.[0]) setVideo(await toDataUri(cam.assets[0].uri));
    } catch { Alert.alert('Camera unavailable', 'Could not open the camera or library.'); }
  };

  const appendFile = async (form: FormData, field: string, uri: string, isVideo: boolean, i: number) => {
    const ext = isVideo ? 'mov' : 'jpg';
    const mime = isVideo ? 'video/quicktime' : 'image/jpeg';
    const name = `storage-${field}-${i}.${ext}`;
    if (Platform.OS === 'web' && (uri.startsWith('data:') || uri.startsWith('blob:'))) {
      const blob = await (await fetch(uri)).blob();
      form.append(field, blob, name);
    } else {
      form.append(field, { uri, type: mime, name } as any);
    }
  };

  const submit = async () => {
    if (photos.length < MIN_PHOTOS) { Alert.alert('Add more photos', `Please add at least ${MIN_PHOTOS} photos.`); return; }
    // The video is OPTIONAL now (still uploaded when the driver takes one). The signature
    // is what confirms the item is physically in the unit.
    const sig = signature || (typedName.trim().length >= 3 ? `typed:${typedName.trim()}` : '');
    if (!sig) { Alert.alert('Sign to confirm', 'Please sign to confirm the item is in storage.'); return; }
    setSubmitting(true);
    try {
      const form = new FormData();
      // Per-file guard: one unreadable URI must not abort the whole upload (mirrors the
      // checklist fix). Count what actually attached.
      let attached = 0;
      for (let i = 0; i < photos.length; i++) {
        try { await appendFile(form, 'photos', photos[i], false, i); attached++; }
        catch (e: any) { console.warn('storage photo skipped:', e?.message || e); }
      }
      if (video) {
        try { await appendFile(form, 'video', video, true, 0); attached++; }
        catch (e: any) { console.warn('storage video skipped:', e?.message || e); }
      }
      if (attached === 0) throw new Error('Could not read the captured media. Please re-add and try again.');
      // Signature rides as a plain body field (data URI or "typed:<name>"), which is what
      // the confirm-pickup route reads when no signature FILE is attached.
      form.append('signature', sig);
      await api.post(`/storage/driver/${id}/confirm-pickup`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000,
      });
      AsyncStorage.removeItem(PERSIST_KEY).catch(() => {}); // submitted — clear snapshot
      Alert.alert('Stored', 'Drop-off confirmed. The item is now in storage and your monthly pay has started.', [
        { text: 'OK', onPress: () => navigation.goBack() },
      ]);
    } catch (e: any) {
      Alert.alert('Upload failed', e?.response?.data?.error || e?.message || 'Please check your connection and try again.');
    } finally { setSubmitting(false); }
  };

  const hasSignature = !!signature || typedName.trim().length >= 3;
  const ready = photos.length >= MIN_PHOTOS && hasSignature;

  return (
    <ScrollView style={styles.screen} contentContainerStyle={{ padding: 16, paddingBottom: 48 }}>
      <TouchableOpacity onPress={() => navigation.goBack()} style={styles.back}><Ionicons name="chevron-back" size={22} color={RD.text} /><Text style={styles.backTxt}>Back</Text></TouchableOpacity>
      <Text style={styles.h1}>Confirm drop-off</Text>
      <Text style={styles.sub}>{productTitle}</Text>
      <Text style={styles.note}>Add at least {MIN_PHOTOS} photos of the item in the unit and sign below. A video is optional. This starts the storage clock and your monthly pay.</Text>

      <View style={styles.block}>
        <View style={styles.blockHead}><Text style={styles.blockTitle}>Photos</Text><Text style={[styles.count, { color: photos.length >= MIN_PHOTOS ? RD.money : RD.textMuted }]}>{photos.length}/{MIN_PHOTOS}+</Text></View>
        <View style={styles.thumbs}>
          {photos.map((uri, i) => (
            <View key={i} style={styles.thumbWrap}>
              <Image source={{ uri }} style={styles.thumb} />
              <TouchableOpacity style={styles.remove} onPress={() => setPhotos(p => p.filter((_, idx) => idx !== i))}><Ionicons name="close" size={14} color="#fff" /></TouchableOpacity>
            </View>
          ))}
          <TouchableOpacity style={styles.add} onPress={pickPhoto}><Ionicons name="camera" size={22} color={RD.maroon} /><Text style={styles.addTxt}>Add</Text></TouchableOpacity>
        </View>
      </View>

      <View style={styles.block}>
        <View style={styles.blockHead}><Text style={styles.blockTitle}>Video (optional)</Text><Text style={[styles.count, { color: video ? RD.money : RD.textMuted }]}>{video ? '1/1' : '0/1'}</Text></View>
        {video ? (
          <View style={styles.videoRow}>
            <View style={styles.videoIcon}><Ionicons name="videocam" size={20} color="#fff" /></View>
            <Text style={styles.videoTxt}>Video added</Text>
            <TouchableOpacity onPress={() => setVideo(null)}><Ionicons name="trash-outline" size={18} color={RD.danger} /></TouchableOpacity>
          </View>
        ) : (
          <TouchableOpacity style={styles.add} onPress={pickVideo}><Ionicons name="videocam" size={22} color={RD.maroon} /><Text style={styles.addTxt}>Add video</Text></TouchableOpacity>
        )}
      </View>

      <View style={styles.block}>
        <View style={styles.blockHead}>
          <Text style={styles.blockTitle}>Your signature</Text>
          <Text style={[styles.count, { color: hasSignature ? RD.money : RD.textMuted }]}>{hasSignature ? 'Signed' : 'Required'}</Text>
        </View>
        <Text style={styles.sigHint}>You are signing that you placed this item in the unit. No customer signature is needed on a storage drop.</Text>
        {SignatureCanvas && Platform.OS !== 'web' ? (
          signature ? (
            <View style={styles.videoRow}>
              <View style={styles.videoIcon}><Ionicons name="create" size={20} color="#fff" /></View>
              <Text style={styles.videoTxt}>Signature captured</Text>
              <TouchableOpacity onPress={() => setSignature(null)}><Ionicons name="trash-outline" size={18} color={RD.danger} /></TouchableOpacity>
            </View>
          ) : (
            <View style={styles.sigPad}>
              <SignatureCanvas
                onOK={(b64: string) => setSignature(b64)}
                onEmpty={() => setSignature(null)}
                descriptionText=""
                clearText="Clear"
                confirmText="Save"
                webStyle={'.m-signature-pad--footer {margin:0;} .m-signature-pad {box-shadow:none;border:none;}'}
              />
            </View>
          )
        ) : (
          <TextInput
            style={styles.sigInput}
            value={typedName}
            onChangeText={setTypedName}
            placeholder="Type your full name to sign"
            placeholderTextColor={RD.textMuted}
            autoCapitalize="words"
          />
        )}
      </View>

      <Button title={submitting ? 'Uploading…' : 'Submit and confirm drop-off'} variant="maroon" size="lg" fullWidth
        icon="checkmark-circle" disabled={!ready || submitting} loading={submitting} onPress={submit} />
      {submitting && <View style={styles.uploading}><ActivityIndicator color={RD.maroon} /><Text style={styles.uploadingTxt}>Uploading media, this can take a moment…</Text></View>}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: RD.bg },
  back: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  backTxt: { fontFamily: fonts.medium, fontSize: 15, color: RD.text },
  h1: { fontFamily: fonts.semiBold, fontSize: 23, color: RD.ink },
  sub: { fontFamily: fonts.medium, fontSize: 14, color: RD.textSec, marginTop: 2 },
  note: { fontFamily: fonts.regular, fontSize: 13, color: RD.textMuted, marginTop: 8, marginBottom: 14, lineHeight: 19 },
  block: { backgroundColor: RD.surface, borderRadius: 16, borderWidth: 1, borderColor: RD.border, padding: 14, marginBottom: 14 },
  blockHead: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  blockTitle: { fontFamily: fonts.semiBold, fontSize: 15, color: RD.text },
  count: { fontFamily: fonts.semiBold, fontSize: 13 },
  thumbs: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  thumbWrap: { position: 'relative' },
  thumb: { width: 80, height: 80, borderRadius: 10, backgroundColor: RD.border },
  remove: { position: 'absolute', top: -6, right: -6, backgroundColor: RD.danger, borderRadius: 11, width: 22, height: 22, alignItems: 'center', justifyContent: 'center' },
  add: { width: 80, height: 80, borderRadius: 10, borderWidth: 1.5, borderColor: RD.maroon, borderStyle: 'dashed', alignItems: 'center', justifyContent: 'center', backgroundColor: RD.maroonSoft },
  addTxt: { fontFamily: fonts.medium, fontSize: 11, color: RD.maroon, marginTop: 2 },
  videoRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  videoIcon: { width: 40, height: 40, borderRadius: 10, backgroundColor: RD.ink, alignItems: 'center', justifyContent: 'center' },
  videoTxt: { flex: 1, fontFamily: fonts.medium, fontSize: 14, color: RD.text },
  sigHint: { fontFamily: fonts.regular, fontSize: 12, color: RD.textMuted, marginBottom: 10, lineHeight: 17 },
  sigPad: { height: 180, borderWidth: 1, borderColor: RD.border, borderRadius: 10, overflow: 'hidden', backgroundColor: '#fff' },
  sigInput: { borderWidth: 1, borderColor: RD.border, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 12, fontFamily: fonts.medium, fontSize: 15, color: RD.text, backgroundColor: RD.bg },
  uploading: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 12, justifyContent: 'center' },
  uploadingTxt: { fontFamily: fonts.regular, fontSize: 12, color: RD.textMuted },
});
